import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationAdmin } from '@/hooks/useOrganizationAdmin';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User, ArrowLeft, Save, Lock } from 'lucide-react';
import type { Profile } from '@/types/database';

const OnboardingProfile = () => {
  const { user } = useAuth();
  const { canEditOrganization, loading: adminLoading } = useOrganizationAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailEditing, setEmailEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [profile, setProfile] = useState<Partial<Profile>>({
    first_name: '',
    last_name: '',
    address: '',
    
    role: 'standard'
  });

  useEffect(() => {
    fetchProfile();
    // Initialize email for editing
    if (user?.email) {
      setNewEmail(user.email);
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error);
        toast.error('Fehler beim Laden des Profils');
        return;
      }

      if (data) {
        setProfile(data);
      } else {
        // Falls kein Profil-Eintrag existiert, verwende user_metadata
        const userMeta = user.user_metadata;
        setProfile({
          first_name: userMeta?.first_name || '',
          last_name: userMeta?.last_name || '',
          address: userMeta?.address || '',
          role: 'standard'
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          ...profile,
          role: 'standard'
        });

      if (error) throw error;

      toast.success('Profil erfolgreich gespeichert');
      navigate('/onboarding');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Fehler beim Speichern des Profils');
    } finally {
      setSaving(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    // Basic format check
    if (!emailRegex.test(email)) {
      return false;
    }
    
    // Additional checks for common invalid patterns
    const localPart = email.split('@')[0];
    const domain = email.split('@')[1];
    
    // Check if local part is too simple (like just "admin")
    if (localPart.length < 3 || /^(admin|test|user)$/.test(localPart.toLowerCase())) {
      return false;
    }
    
    // Check if domain is too simple
    if (domain.split('.').length < 2 || domain.split('.')[0].length < 3) {
      return false;
    }
    
    return true;
  };

  const saveEmail = async () => {
    if (!user || !newEmail) return;

    // Validate email format
    if (!validateEmail(newEmail)) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein. Verwende eine vollständige E-Mail-Adresse (z.B. max.mustermann@example.com)');
      return;
    }

    // Check if email is actually different
    if (newEmail === user.email) {
      toast.error('Die neue E-Mail-Adresse ist identisch mit der aktuellen.');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) {
        if (error.message.includes('email_address_invalid')) {
          toast.error('Die E-Mail-Adresse ist ungültig. Bitte verwende eine vollständige E-Mail-Adresse (z.B. max.mustermann@example.com)');
        } else if (error.message.includes('email_address_in_use')) {
          toast.error('Diese E-Mail-Adresse wird bereits verwendet.');
        } else {
          toast.error('Fehler beim Aktualisieren der E-Mail: ' + error.message);
        }
        return;
      }

      toast.success('E-Mail erfolgreich aktualisiert. Bitte bestätige die neue E-Mail-Adresse.');
      setEmailEditing(false);
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error('Unerwarteter Fehler beim Aktualisieren der E-Mail');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/onboarding')}
            className="mb-4"
            disabled={!canEditOrganization}
            title={!canEditOrganization ? "Nur Admins können das Organisations-Onboarding durchführen" : ""}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zum Onboarding
          </Button>
        </div>

        <Card className="shadow-xl border-0 bg-card/50 backdrop-blur-sm">
          <CardHeader className="text-center">
            <User className="mx-auto h-12 w-12 text-primary mb-4" />
            <CardTitle className="text-2xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Onboarding - Schritt 1 von 4
            </CardTitle>
            <CardDescription>
              Bearbeite deine Profilinformationen für das Onboarding
            </CardDescription>
            <div className="w-full bg-muted rounded-full h-2 mt-4">
              <div 
                className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full transition-all duration-300"
                style={{ width: "25%" }}
              />
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* E-Mail Sektion */}
            <Card className="border-2 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-5 w-5" />
                  E-Mail-Adresse
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!emailEditing ? (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{user?.email}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEmailEditing(true)}
                    >
                      Bearbeiten
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Label htmlFor="email">Neue E-Mail-Adresse</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="max.mustermann@example.com"
                    />
                    <p className="text-xs text-muted-foreground">
                      Verwende eine vollständige E-Mail-Adresse mit Vor- und Nachname
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        onClick={saveEmail}
                        disabled={saving || newEmail === user?.email || !validateEmail(newEmail)}
                        size="sm"
                      >
                        {saving ? 'Speichere...' : 'E-Mail ändern'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setEmailEditing(false);
                          setNewEmail(user?.email || '');
                        }}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Persönliche Informationen */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-5 w-5" />
                  Persönliche Informationen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Vorname</Label>
                    <Input
                      id="firstName"
                      value={profile.first_name || ''}
                      onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                      placeholder="Max"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nachname</Label>
                    <Input
                      id="lastName"
                      value={profile.last_name || ''}
                      onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                      placeholder="Mustermann"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input
                    id="address"
                    value={profile.address || ''}
                    onChange={(e) => setProfile({...profile, address: e.target.value})}
                    placeholder="Musterstraße 123, 12345 Musterstadt"
                  />
                </div>

              </CardContent>
            </Card>

            {/* Team-Informationen */}

            {/* Aktions-Buttons */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline"
                onClick={() => navigate('/onboarding')}
                className="flex-1"
                disabled={!canEditOrganization}
                title={!canEditOrganization ? "Nur Admins können das Organisations-Onboarding durchführen" : ""}
              >
               ← Onboarding
              </Button>
              
              <Button 
                onClick={saveProfile}
                disabled={saving}
                className="flex-1"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Speichere...' : 'Speichern'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingProfile;